import fetch from "@lib/fetch";

import { buildFetchRequestArgs } from "../formData";
import { reactQueryPostWebsocketAdapter } from "../reactQueryFetcherAdapters";
import { generativeAiAppGroupIdAgentPreview } from "./constants";
import type {
  PostwsGenerativeAiAppGroupIdAgentPreviewPayload,
  PostwsGenerativeAiAppGroupIdAgentPreviewProps,
} from "./types";
import { isAgentPreviewAsyncResult } from "./validators";

export const postwsGenerativeAiAppGroupIdAgentPreview = reactQueryPostWebsocketAdapter(
  generativeAiAppGroupIdAgentPreview,
  async (
    signal,
    {
      appGroupId,
      agentDefinition,
      testPrompt,
      userId,
      randomUser,
      customUserHash,
      triggerPropertiesHash,
      campaignId,
      canvasId,
      scope,
      stepId,
      briefFile,
      contextFiles,
      jobToken,
      mockResponseLength,
    }: PostwsGenerativeAiAppGroupIdAgentPreviewProps
  ): Promise<{}> => {
    // Any file -- a brief or staged context files -- makes this a multipart request, and
    // multipart can only carry the definition as one JSON-encoded part: bracket-flattening drops
    // an empty array entirely, and the backend applies the definition by key presence, so an
    // emptied ruleset or file list would silently read as "keep whatever is saved" rather than
    // as the edit on screen. A file-less preview stays plain JSON, sending the same key as a
    // nested object -- the backend accepts either shape under agent_definition.
    const staged = contextFiles ?? [];
    const isMultipart = (briefFile !== undefined && briefFile !== null) || staged.length > 0;

    const payload: PostwsGenerativeAiAppGroupIdAgentPreviewPayload = {
      job_token: jobToken,
      agent_definition: isMultipart ? JSON.stringify(agentDefinition) : agentDefinition,
      test_prompt: testPrompt,
      user_id: userId,
      random_user: randomUser,
      custom_user_hash: customUserHash,
      trigger_properties_hash: triggerPropertiesHash,
      campaign_id: campaignId,
      canvas_id: canvasId,
      scope: scope,
      step_id: stepId,
      mock_response_length: mockResponseLength,
    };
    const { params, options, setContentType } = buildFetchRequestArgs(payload, briefFile, "brief_file", {
      key: "context_files",
      files: staged,
    });

    return await fetch(
      `/${appGroupId}/generative_ai/agent/preview`,
      "POST",
      params,
      { signal, ...options },
      {
        setContentType,
        propagateFetchRequestError: false,
      }
    );
  },
  isAgentPreviewAsyncResult
);
